import { uiSurface } from "shared/ui/uiSurface"
import { InboxOutlined } from "@mui/icons-material"
import { Box, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Virtuoso } from "react-virtuoso"
import { OrderViewModel } from "src/db/Order"
import {
  CommonQueryFilter,
  CommonSorting,
} from "src/features/order/components/CommonQueryFilter"
import { useOrderFilter } from "src/features/order/hooks"
import { SkeletonWrapper } from "src/features/shared-module/components"
import {
  appConstants,
  FilterState,
  OrderStatusEnum,
} from "src/features/shared-module/constants"
import { orderSortingFunction } from "src/features/shared-module/functions"
import { useAppSelector } from "../hooks"
import {
  isLoadingSelector,
  isOrdersLoadingSelector,
  orderListByStateSelector,
} from "../slices/dashboard.slice"
import { OrderCard } from "./OrderCard"
import { OrderStatusIcon } from "./OrderStatusIcon"
import { VirtualizedSortableOrderList } from "./VirtualizedSortableOrderList"

export const OrdertListByStatus: FC<{ expanded: boolean }> = ({ expanded }) => {
  const stateList = [
    OrderStatusEnum.ON_THE_WAY_TO_CUSTOMER,
    OrderStatusEnum.ON_THE_WAY_TO_PROVIDER,
    OrderStatusEnum.COMPLETED,
    OrderStatusEnum.READY_FOR_HAULAGE,
  ]
  const isLoading = useAppSelector(isLoadingSelector)
  const isOrdersLoading = useAppSelector(isOrdersLoadingSelector)
  const ordersByState = useAppSelector(orderListByStateSelector)

  const scrollingOrder = useAppSelector(
    (state) => state.dashboard.scrollingDetail,
  )
  const [currentTabIndex, setTabStatus] = useState<number>(0)

  useEffect(() => {
    if (
      scrollingOrder?.state === OrderStatusEnum.READY_FOR_HAULAGE &&
      currentTabIndex !== 3
    ) {
      setTabStatus(3)
    }
  }, [scrollingOrder, currentTabIndex])

  const handleTabChange = (tab: number) => {
    setTabStatus(tab)
  }

  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        {Array.from({ length: 4 }, (e, i) => i).map((e: number) => {
          return (
            <SkeletonWrapper
              key={e}
              height={56}
              sxProps={{
                borderRadius: 2,
              }}
            />
          )
        })}
      </Stack>
    )
  }

  return (
    <Stack
      {...uiSurface("UI-DSP-016")}
      spacing={1.5}
      sx={{
        height: "calc(100% - 40px)",
      }}
    >
      <Tabs
        {...uiSurface("UI-DSP-017")}
        data-testid="orders-status-tabs"
        value={currentTabIndex}
        onChange={(event, value) => handleTabChange(value)}
        aria-label="order by state tabs"
        orientation={expanded ? "horizontal" : "vertical"}
        textColor="primary"
        indicatorColor="primary"
        sx={(theme) => ({
          minHeight: 76,
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: 99,
            backgroundColor: theme.palette.primary.main,
          },
          "&.MuiTabs-vertical": {
            "& .MuiTabs-indicator": {
              opacity: 0,
            },
          },
          "& .MuiTab-root": {
            transition: "all 0.2s ease",
            borderRadius: 2,
            minHeight: 76,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          },
        })}
      >
        {stateList.map((status, index) => {
          return (
            <Tab
              data-testid={`orders-status-tab-${status}`}
              key={`tab-head-${index}`}
              label={
                <OrderCount
                  status={status}
                  orderCount={ordersByState[status].length}
                />
              }
              value={index}
            />
          )
        })}
      </Tabs>
      {expanded && isOrdersLoading && (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress size={32} />
        </Stack>
      )}
      {expanded && !isOrdersLoading &&
        stateList.map((state, index) => {
          return (
            currentTabIndex === index && (
              <IndividualListingByState
                key={`state-listing-${state}`}
                orderList={ordersByState[state]}
                state={state}
              />
            )
          )
        })}
    </Stack>
  )
}

const IndividualListingByState: FC<{
  orderList: OrderViewModel[]
  state: OrderStatusEnum
}> = ({ state, orderList }) => {
  const { t } = useTranslation()
  const { filterState, actions } = useOrderFilter()
  const seachText = (filterState.query || "").toLowerCase()

  const orderList1 = useMemo(() => {
    const list = orderList.filter((order) => {
      return (
        order.oneLineAddress?.toLowerCase().includes(seachText) ||
        order.reference?.toLowerCase().includes(seachText) ||
        order.amount?.toString()?.toLowerCase().includes(seachText) ||
        order.version?.toString()?.toLowerCase().includes(seachText) ||
        order.client.firstName?.toLowerCase().includes(seachText) ||
        order.client.lastName?.toLowerCase().includes(seachText)
      )
    })
    if (filterState.sortBy) {
      list.sort(orderSortingFunction(filterState.sortBy, filterState.sortOrder))
    }
    return list
  }, [orderList, filterState.sortBy, filterState.sortOrder, seachText])

  return (
    <Box
      {...uiSurface("UI-DSP-018")}
      sx={{
        height: "100%",
      }}
    >
      <Box sx={{ marginBottom: 1 }}>
        <FilterHeader filterState={filterState} actions={actions} />
      </Box>
      <Stack
        spacing={1}
        sx={{
          height: "calc(100% - 138px)",
          overflow: "auto",
          paddingX: 0.5,
        }}
      >
        {orderList1.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={(theme) => ({
              py: 6,
              px: 3,
              borderRadius: 3,
              border: `1px dashed ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover,
            })}
          >
            <InboxOutlined
              sx={(theme) => ({
                fontSize: 40,
                color: theme.palette.text.disabled,
              })}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              {t("noOrdersInStatus")}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.disabled", textAlign: "center" }}
            >
              {t("noOrdersHint")}
            </Typography>
          </Stack>
        ) : state === OrderStatusEnum.READY_FOR_HAULAGE ? (
          <VirtualizedSortableOrderList
            contextId={`READY_FOR_HAULAGE`}
            items={orderList1}
          />
        ) : (
          <VirtualizedOrderList orders={orderList1} />
        )}
      </Stack>
    </Box>
  )
}

const VirtualizedOrderList: FC<{ orders: OrderViewModel[] }> = ({ orders }) => {
  const renderItem = useCallback(
    (index: number) => (
      <Box sx={{ pb: 1 }}>
        <OrderCard
          key={orders[index].id}
          order={orders[index]}
          showDivider={false}
        />
      </Box>
    ),
    [orders],
  )

  return (
    <Virtuoso
      totalCount={orders.length}
      itemContent={renderItem}
      style={{ height: "100%" }}
      fixedItemHeight={appConstants.orderRawHeight + 8}
    />
  )
}

const statusI18nKeys: Record<OrderStatusEnum, string> = {
  [OrderStatusEnum.ON_THE_WAY_TO_CUSTOMER]: "statusToCustomer",
  [OrderStatusEnum.ON_THE_WAY_TO_PROVIDER]: "statusToProvider",
  [OrderStatusEnum.COMPLETED]: "statusCompleted",
  [OrderStatusEnum.READY_FOR_HAULAGE]: "statusToDispatch",
}

const statusColors: Record<OrderStatusEnum, string> = {
  [OrderStatusEnum.ON_THE_WAY_TO_CUSTOMER]: "#0B7285",
  [OrderStatusEnum.ON_THE_WAY_TO_PROVIDER]: "#F08C6A",
  [OrderStatusEnum.COMPLETED]: "#43A047",
  [OrderStatusEnum.READY_FOR_HAULAGE]: "#1A2A35",
}

const OrderCount: FC<{
  status: OrderStatusEnum
  orderCount: number
}> = ({ orderCount, status }) => {
  const { t } = useTranslation()
  const color = statusColors[status]
  return (
    <Stack spacing={0.5} alignItems={"center"}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontSize: "0.6rem",
          color: "text.secondary",
          lineHeight: 1.2,
        }}
      >
        {t(statusI18nKeys[status])}
      </Typography>
      <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
        <OrderStatusIcon status={status} />
        <Typography
          variant="subtitle2"
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: 99,
            fontWeight: 700,
            fontSize: "0.75rem",
            fontVariantNumeric: "tabular-nums",
            minWidth: 40,
            textAlign: "center",
            backgroundColor: `${color}14`,
            color: color,
            border: `1px solid ${color}30`,
          }}
        >
          {orderCount}
        </Typography>
      </Stack>
    </Stack>
  )
}

const FilterHeader: FC<{
  filterState: FilterState
  actions: any
}> = ({ filterState, actions }) => {
  const { t } = useTranslation()
  return (
    <Stack {...uiSurface("UI-DSP-019")} data-testid="orders-filter-header" direction={"row"} spacing={1}>
      <CommonQueryFilter
        initialQuery={filterState.query}
        onChangeQuery={actions.setFilterQuery}
      />
      <CommonSorting
        options={[
          {
            label: t("sortByAddress"),
            value: "oneLineAddress",
          },
          {
            label: t("sortByOrderId"),
            value: "id",
          },
        ]}
        onChangeSort={(value: string) =>
          actions.setSorting({
            sortBy: value,
            sortOrder: filterState.sortOrder === "asc" ? "desc" : "asc",
          })
        }
      />
    </Stack>
  )
}
